import { useEffect, useState, useCallback, useRef } from 'react';
import contactService from '@/services/contact/contactService';
import customFieldService from '@/services/setting/customFieldService';
import bulkDeleteService from '@/services/bulk-delete/bulkDeleteService';
import { useAbility } from '@/providers/';
import { pendingApprovalMessage, exportErrorMessage } from '@/utils/exportResponse';
import { exportPayload } from './contactExportSelection';
import { toast } from 'sonner';
import { KeenIcon, DefaultTooltip } from '@/components';
import { Can } from '@casl/react';
import { Link, useNavigate } from 'react-router-dom';
import { Container } from '@/components/container';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useToolbar } from '@/providers/ToolbarProvider';
import { ConfirmationModal } from '@/components/modal';
import { useConfirmationModal } from '@/hooks';
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-select-dt';
import 'datatables.net-responsive-dt';
import { createRoot } from 'react-dom/client';
import { SelectInput, DateInput, TextInput, STATUS_OPTIONS, STATUS_NUMBER_OPTIONS, FilterContainer, ColumnToggleDropdown } from '@/components/TableFilter'
import { cn } from '@/lib/utils';
import { renderMultiAvatars, FiltersToFormData, getAccessToken, normalizeFieldName, decodeHtmlEntities } from '@/utils';
import { createDataTableOptions, createDataTableConfig } from '@/utils';
import { formatAddressList, formatAddressField, toLines } from '@/utils';
import dynamicExportService from '@/services/dynamicExporter/dynamicExportService';
import { useAuthContext } from '@/auth';
import { useUserOptions, useContactTypeOptions, useTagOptions } from '@/hooks';
import useDataTableBulkDelete from '@/hooks/useDataTableBulkDelete';
import { BulkDeleteActions } from '@/components/BulkDeleteActions';
import { BulkEditModal, YES_NO_OPTIONS } from '@/components/bulk-edit/BulkEditModal';
import { describeBulkEditResult } from '@/components/bulk-edit/bulkEditPayload';
import { ChevronDown } from 'lucide-react';


DataTable.use(DT);

const ContactNameCell = ({ fullName, id, onNavigate }) => {
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    setOpen(false);
    onNavigate(id);
  };

  return (
    <DefaultTooltip
      title={fullName}
      placement="top"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      disableInteractive
    >
      <span onClick={handleClick} className='cursor-pointer link'>
        {fullName}
      </span>
    </DefaultTooltip>
  );
};

const Contacts = () => {
  const { auth, logout } = useAuthContext();
  const { ability } = useAbility();
  // Downloads need the Export Data permission (OL-154).
  const canExport = ability.can('export', 'data');
  const loadUserOptions = useUserOptions({ defaultPageSize: 20 });
  const loadContactTypeOptions = useContactTypeOptions({ defaultPageSize: 20 });
  const loadTagOptions = useTagOptions({ defaultPageSize: 20, type: 'contact', });
  const Navigate = useNavigate()
  const { setConfig } = useToolbar();
  const [exportContactLoading, setExportContactLoading] = useState(false)
  const { isOpen, actionId, openModal, closeModal } = useConfirmationModal();
  const [showFilter, setShowFilter] = useState(false)
  const [filterData, setFilterData] = useState({
    created_by: null,
    assigned_to: null,
    status: null,
    created_at: null,
    allow_secure_messaging: null,
    tags: null,
    from_date: null,
    to_date: null,
    date_range: null,
    state: null,
    zip: null,
    contact_type: null
  });
  const tableRef = useRef(null);
  const token = getAccessToken();
  const filterRef = useRef(filterData);
  const canEditContact = ability.can('handle', 'contact')
  const canDeleteContact = ability.can('delete', 'contact')
  const formikRef = useRef();
  const [contactRecordsTotal, setContactRecordsTotal] = useState(null);
  const [contactRecordsFiltered, setContactRecordsFiltered] = useState(0);
  const [contactTableLoaded, setContactTableLoaded] = useState(false);
  // "Select all N that match": the count and the query the list was loaded with (OL-206).
  const [allMatching, setAllMatching] = useState(null);
  const {
    selectedRows,
    allSelected,
    bulkDeleteLoading,
    bulkDeleteOpen,
    openBulkDelete,
    closeBulkDelete,
    confirmBulkDelete,
    toggleSelectPage,
  } = useDataTableBulkDelete({
    tableRef,
    model: 'Contact',
    deleteFn: bulkDeleteService.bulkDelete,
    onSuccess: () => {
      refresh();
      toast.success('Contacts Deleted', {
        description: 'Selected contacts deleted successfully and moved to the recycle bin.',
      });
    },
    onError: (error) => {
      console.error('Error', error)
      if (error?.message === `Can't delete: Matter records are related to this Contact.`) {
        toast.error('Unable to delete Contact.', {
          description: `Can't delete: Matter records are related to this Contact.`,
        });
        return;
      }
      toast.error('Oops! Something went wrong.', {
        description: error?.message || 'An error occurred while deleting contacts. Please try again in a moment.',
      });
    },
  });

  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditLoading, setBulkEditLoading] = useState(false);
  const bulkEditFields = [
    { name: 'contact_type_id', label: 'Contact Type', loadOptions: loadContactTypeOptions },
    { name: 'assigned_to', label: 'Assigned To', loadOptions: loadUserOptions, isMulti: true },
    { name: 'status', label: 'Status', options: STATUS_OPTIONS },
    { name: 'allow_secure_messaging', label: 'Secure Messaging', options: YES_NO_OPTIONS, boolean: true },
    { name: 'allow_file_upload', label: 'File Upload', options: YES_NO_OPTIONS, boolean: true },
  ];

  const handleBulkEdit = async (changes) => {
    setBulkEditLoading(true);
    try {
      const { data } = await contactService.bulkUpdateContacts({ ids: selectedRows.map((row) => row.id), ...changes });
      setBulkEditOpen(false);
      tableRef.current?.dt?.()?.rows().deselect();
      refresh();
      const { title, description } = describeBulkEditResult(data, 'contact');
      toast.success(title, description ? { description } : undefined);
    } catch (error) {
      toast.error('Bulk edit failed.', { description: error?.message || 'Please try again.' });
    } finally {
      setBulkEditLoading(false);
    }
  };

  useEffect(() => {
    filterRef.current = filterData;
  }, [filterData]);

  const baseColumns = [
    {
      data: null,
      defaultContent: '',
      className: 'dt-row-select cursor-pointer text-center',
      id: 'contact-checkbox-select',
      orderable: false,
      name: 'checkbox',
      render: function () {
        // Real styled checkbox so row selection matches the header "select all"
        // checkbox. pointer-events-none lets the click fall through to the cell,
        // which DataTables Select uses to toggle selection (see select.selector).
        return '<input type="checkbox" tabindex="-1" aria-label="Select row" class="checkbox checkbox-sm form-checkbox row-select-checkbox pointer-events-none" />';
      },
    },
    {
      title: 'Name',
      data: 'first_name',
      name: 'first_name',
      searchable: true,
      render: function (data, type, row) {
        return `<div class="w-[180px] whitespace-pre-wrap truncate" id="contact-cell-${row.id}"></div>`;
      }
    },
    { title: 'Email', name: 'email', data: 'email', defaultContent: '-' },
    // { title: 'Contact ID', name: 'number', data: 'number', defaultContent: '-' },
    {
      title: 'Contact Type',
      name: 'contact_type',
      data: 'contact_type',
      orderable: true,
      render: value => (value ? value.name : '-'),
      defaultContent: '-',
    },
    {
      title: 'Assigned To',
      name: 'assigned_to',
      data: 'assigned_to',
      orderable: true,
      searchable: false,
      render: function (data, type, row) {
        return `<div id="assigned-to-${row.id}"></div>`;
      }
    },
    {
      title: 'Subscription Status',
      name: 'is_unsubscribe',
      data: 'is_unsubscribe',
      orderable: true,
      render: function (value) {
        const badgeClass = value === 0 ? '' : 'badge-warning';
        const label = value === 0 ? '-' : 'Unsubscribed';
        return `<span class="${value === 0 ? '' : 'badge badge-pill badge-outline'} ${badgeClass} gap-1 items-center">
            <span class="${value === 0 ? '' : 'badge badge-dot size-1.5'} ${badgeClass}"></span>
            ${label}
          </span>`
      }
    },
    {
      data: 'tags',
      name: 'tags',
      title: 'Tags',
      className: 'whitespace-normal',
      orderable: true,
      render: function (data, type, row) {
        if (!Array.isArray(data)) return '';
        const tagSpans = data
          .map(tag => `<span class="capitalize whitespace-nowrap badge badge-pill badge-outline badge-primary mr-1 mb-1">${tag.name}</span>`)
          .join('');
        return tagSpans;
      }
    },
    {
      data: 'status',
      name: 'status',
      title: 'Status',
      orderable: true,
      render: function (value) {
        const badgeClass = value === 'active' ? 'badge-success' : 'badge-danger';
        const label = value === 'active' ? 'Active' : 'Inactive';
        return `<span class="badge badge-pill badge-outline ${badgeClass} gap-1 items-center">
            <span class="badge badge-dot size-1.5 ${badgeClass}"></span>
            ${label}
          </span>`
      }
    },
    {
      data: 'allow_secure_messaging',
      name: 'allow_secure_messaging',
      title: 'Secure Messaging',
      orderable: true,
      render: function (value) {
        const badgeClass = value === 0 ? 'badge-danger' : 'badge-success';
        const label = value === 0 ? 'Disabled' : 'Enabled';
        return `
          <span class="badge badge-pill badge-outline ${badgeClass} gap-1 items-center">
            <span class="badge badge-dot size-1.5 ${badgeClass}"></span>
            ${label}
          </span>`

      }
    },
    {
      orderable: true, title: 'Created At', name: 'created_at', data: 'created_at',
      defaultContent: '-'
    },
    {
      orderable: true, title: 'Business Name', name: 'business_name', data: 'business_name',
      defaultContent: '-'
    },
    // {
    //   orderable: true, title: 'Company Name', name: 'company_name', data: 'company_name',
    //   defaultContent: '-'
    // },
    {
      orderable: true, title: 'Speciality', name: 'speciality', data: 'speciality',
      defaultContent: '-'
    },
    {
      orderable: true, title: 'DOB', name: 'dob', data: 'dob',
      defaultContent: '-'
    },
    {
      orderable: true, title: 'DOL', name: 'dol', data: 'dol',
      defaultContent: '-'
    },
    {
      orderable: true, title: 'Home Number', name: 'home_number', data: 'home_number',
      defaultContent: '-'
    },
    {
      orderable: true, title: 'Office Number', name: 'office_number', data: 'office_number',
      defaultContent: '-'
    },
    {
      orderable: true, title: 'Fax Number', name: 'fax_number', data: 'fax_number',
      defaultContent: '-'
    },
    {
      orderable: true, title: 'Website', name: 'website', data: 'website',
      defaultContent: '-'
    },
    {
      title: 'Invoice Template',
      name: 'invoice_template',
      data: 'invoice_template',
      render: value => (value ? value.name : '-'),
      orderable: true,
      defaultContent: '-',
    },
    {
      title: 'Phone Number',
      name: 'phones',
      data: 'phones',
      render: (value, type, row) => {
        // If value is null, undefined, not an array, or empty → show '-'
        if (!Array.isArray(value) || value.length === 0) {
          return '-';
        }

        return value
          .map((p) => {
            const title = p?.title ? p.title : 'N/A';
            const number = p?.number ? p.number : 'N/A';
            return `${title}: ${number}`;
          })
          .join('<br>');
      },
      orderable: true,
      defaultContent: '-',
    },
    {
      title: 'Address',
      name: 'address',
      data: 'address',
      render: value => toLines(formatAddressList(value)),
      orderable: true,
      defaultContent: '-',
    },
    // City, State and Zip are also available on their own so a partly filled
    // address still reads cleanly and the list can be filtered by state.
    {
      title: 'City',
      name: 'city',
      data: 'address',
      render: value => toLines(formatAddressField(value, 'city')),
      orderable: true,
      defaultContent: '-',
    },
    {
      title: 'State',
      name: 'state',
      data: 'address',
      render: value => toLines(formatAddressField(value, 'state')),
      orderable: true,
      defaultContent: '-',
    },
    {
      title: 'Zip',
      name: 'zip',
      data: 'address',
      render: value => toLines(formatAddressField(value, 'zip')),
      orderable: true,
      defaultContent: '-',
    },
    {
      title: 'Allow File Upload',
      name: 'allow_file_upload',
      data: 'allow_file_upload',
      render: v =>
        v
          ? `<span class="badge badge-pill badge-outline badge-success">Yes</span>`
          : `<span class="badge badge-pill badge-outline badge-danger">No</span>`,
      orderable: true,
      defaultContent: '-',
    },
    {
      orderable: true, title: 'Updated At', name: 'updated_at', data: 'updated_at',
      defaultContent: '-'
    },
  ];

  const actionColumn = {
    title: 'Actions',
    name: 'actions',
    data: null,
    orderable: false,
    searchable: false,
    render: (data, type, row) => `<div id="action-cell-${row.id}"></div>`,
  };

  const baseDefaultVisibleColumns = [
    'checkbox',
    'first_name',
    'email',
    'contact_type',
    'assigned_to',
    // 'is_unsubscribe',
    'status',
    'allow_secure_messaging',
    // 'number',
    // 'tags',
    // Shown separately rather than as one joined Address column: imported
    // contacts are often missing some of the four, and separate columns are
    // what make filtering by state usable.
    'address',
    'city',
    'state',
    'zip',
    'created_at',
    'actions'
  ];

  const [columns, setColumns] = useState(() => [...baseColumns, actionColumn]);
  const [defaultVisibleColumns] = useState(baseDefaultVisibleColumns);
  const [customFieldsLoading, setCustomFieldsLoading] = useState(true);
  const columnOrderStorageKey = typeof window !== 'undefined'
    ? `column-toggle-order:${window.location.pathname}`
    : null;

  const formatCustomFieldValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const decoded = typeof value === 'string' ? decodeHtmlEntities(value) : value;

    if (Array.isArray(decoded)) {
      return decoded.join(', ');
    }

    if (typeof decoded === 'string') {
      const trimmed = decoded.trim();
      const looksJson =
        (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
        (trimmed.startsWith('{') && trimmed.endsWith('}'));

      if (looksJson) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) return parsed.join(', ');
          if (parsed && typeof parsed === 'object') return JSON.stringify(parsed);
          return String(parsed);
        } catch (error) {
          // fall through to raw decoded string
        }
      }

      return decoded;
    }

    return String(decoded);
  };

  const buildCustomFieldColumns = (titles) =>
    titles.map((title) => ({
      title,
      name: `custom_field_${normalizeFieldName(title)}`,
      data: 'custom_fields',
      orderable: false,
      defaultContent: '-',
      render: (data) => {
        if (!Array.isArray(data) || data.length === 0) return '-';
        const field = data.find((f) => f?.title === title);
        if (!field || !field.pivot) return '-';
        return formatCustomFieldValue(field.pivot.value);
      },
    }));

  const buildColumns = (titles) => [...baseColumns, ...buildCustomFieldColumns(titles), actionColumn];

  useEffect(() => {
    if (typeof window !== 'undefined' && columnOrderStorageKey) {
      try {
        const raw = window.localStorage.getItem(columnOrderStorageKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed) && parsed.includes('actions')) {
            const cleaned = parsed.filter((name) => name !== 'actions');
            cleaned.push('actions');
            window.localStorage.setItem(columnOrderStorageKey, JSON.stringify(cleaned));
          }
        }
      } catch (error) {
        // ignore invalid storage
      }
    }

    let isMounted = true;
    const loadCustomFields = async () => {
      try {
        const { data, status } = await customFieldService.getAllCustomFields();
        if (!isMounted) return;

        if (status && Array.isArray(data)) {
          const contactFields = data.filter((f) => f?.type === 'contact');
          const titles = contactFields.map((f) => f?.title).filter(Boolean);
          setColumns(buildColumns(titles));
        } else {
          setColumns([...baseColumns, actionColumn]);
        }
      } catch (error) {
        console.error('Error', error);
        toast.error('Failed to fetch Custom Fields.', {
          description: 'An error occurred while fetching Custom Fields. Please try again later.',
        });
        setColumns([...baseColumns, actionColumn]);
      } finally {
        if (isMounted) setCustomFieldsLoading(false);
      }
    };

    loadCustomFields();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleContactTableData = useCallback((response) => {
    setContactRecordsTotal(response?.recordsTotal ?? 0);
    setContactRecordsFiltered(response?.recordsFiltered ?? 0);
    setContactTableLoaded(true);
  }, []);

  const ajaxConfig = createDataTableConfig(
    '/contacts/data_table_list',
    filterRef,
    token,
    logout,
    handleContactTableData
  );

  const headerCheckboxId = 'contact-table-select-all';

  const syncHeaderCheckbox = useCallback(() => {
    const table = tableRef?.current?.dt?.();
    if (!table) return;
    const headerCheckbox = document.getElementById(headerCheckboxId);
    if (!headerCheckbox) return;
    const totalOnPage = table.rows({ page: 'current' }).count();
    const selectedOnPage = table.rows({ selected: true, page: 'current' }).count();
    headerCheckbox.checked = totalOnPage > 0 && totalOnPage === selectedOnPage;
    headerCheckbox.indeterminate = selectedOnPage > 0 && selectedOnPage < totalOnPage;
  }, [tableRef]);

  // Reflect each row's selection state on its (display-only) checkbox input.
  const syncRowCheckboxes = useCallback(() => {
    const table = tableRef?.current?.dt?.();
    if (!table) return;
    table.rows({ page: 'current' }).every(function () {
      const node = this.node();
      if (!node) return;
      const input = node.querySelector('.row-select-checkbox');
      if (input) input.checked = node.classList.contains('selected');
    });
  }, [tableRef]);

  const setupHeaderCheckbox = useCallback((api) => {
    const headerCell = api.column('checkbox:name').header();
    if (!headerCell) return;
    headerCell.innerHTML = `
      <label class="inline-flex items-center cursor-pointer">
        <input id="${headerCheckboxId}" type="checkbox" class="checkbox checkbox-sm cursor-pointer form-checkbox" />
      </label>
    `;
    const headerCheckbox = headerCell.querySelector(`#${headerCheckboxId}`);
    if (headerCheckbox) {
      headerCheckbox.addEventListener('change', (event) => {
        event.stopPropagation();
        const table = tableRef?.current?.dt?.();
        if (!table) return;
        if (event.target.checked) {
          table.rows({ page: 'current' }).select();
        } else {
          table.rows({ page: 'current' }).deselect();
        }
        syncHeaderCheckbox();
      });
    }
  }, [syncHeaderCheckbox, toggleSelectPage]);

  const options = createDataTableOptions({
    order: [[9, 'desc']],
    select: {
      style: 'multi',
      selector: 'td.dt-row-select'
    },
    initComplete: function () {
      const api = this.api();
      setupHeaderCheckbox(api);
      api.on('select deselect draw', () => {
        syncHeaderCheckbox();
        syncRowCheckboxes();
      });
      // Unticking a row, or a new page, search or filter, ends "all matching".
      api.on('deselect draw', () => setAllMatching(null));
      api.on('page', () => {
        api.rows().deselect();
      });
      const visibilityKey = columnOrderStorageKey ? `${columnOrderStorageKey}:visible` : null;
      let savedVisible = null;
      if (visibilityKey && typeof window !== 'undefined') {
        try {
          const raw = window.localStorage.getItem(visibilityKey);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) savedVisible = parsed;
          }
        } catch {
          savedVisible = null;
        }
      }

      api.columns().every(function () {
        const col = this;
        const name = col.settings()[0].aoColumns[col.index()].name;
        if (!name) return;
        if (savedVisible) {
          col.visible(savedVisible.includes(name), false);
          return;
        }
        if (!defaultVisibleColumns.includes(name)) {
          col.visible(false, false);
        }
      });
      api.columns.adjust();
      syncHeaderCheckbox();
    },
    drawCallback: function () {
      const api = this.api();
      const rows = api.rows({ page: 'current' }).data();
      syncHeaderCheckbox();
      syncRowCheckboxes();

      rows.each((row) => {
        const container = document.getElementById(`assigned-to-${row.id}`);
        if (container) {
          if (!container._reactRoot) {
            container._reactRoot = createRoot(container);
          }
          container._reactRoot.render(renderMultiAvatars(row.assigned_to));
        }

        const contactContainer = document.getElementById(`contact-cell-${row.id}`);
        if (contactContainer) {
          if (!contactContainer._reactRoot) {
            contactContainer._reactRoot = createRoot(contactContainer);
          }
          contactContainer._reactRoot.render(renderContactName(row));
        }

        const actionContainer = document.getElementById(`action-cell-${row.id}`);
        if (actionContainer) {
          if (!actionContainer._reactRoot) {
            actionContainer._reactRoot = createRoot(actionContainer);
          }
          actionContainer._reactRoot.render(renderCustomActions(row));
        }
      });
    },
  });

  const resetFilters = () => {
    const cleared = {
      created_by: null,
      assigned_to: null,
      status: null,
      created_at: null,
      allow_secure_messaging: null,
      tags: null,
      from_date: null,
      to_date: null,
      date_range: null,
      state: null,
      zip: null,
      contact_type: null
    };

    setFilterData(cleared);
    filterRef.current = cleared;
    refresh()
  };

  const handleFilterChange = (key, val) => {
    setFilterData((prev) => {
      const updated = { ...prev, [key]: val };
      filterRef.current = updated;
      //console.log('updated',updated)
      return updated;
    });
  };

  const handleToggleFilter = () => {
    setShowFilter(prev => !prev)
  }

  const refresh = () => {
    setContactTableLoaded(false);
    const table = tableRef.current.dt();
    table.ajax.reload();
  };

  const canShowAddContactButton =
    contactTableLoaded &&
    ability.can('handle', 'contact');

  const handleDelete = async () => {
    closeModal();
    try {
      const { status, data } = await contactService.deleteContact(actionId);
      if (status) {
        refresh()
        toast.success('Deleted Contact.', {
          description: `${data.first_name + ' ' + data.last_name} Contact deleted Successfully and moved to the recycle bin.`,

        });
      }
    } catch (error) {
      console.error('Error', error)
      if (error.message === `Can't delete: Matter records are related to this Contact.`) {
        toast.error('Unable to delete Contact.', {
          description: `Can't delete: Matter records are related to this Contact.`,
        });
        return
      }
      toast.error('Oops! Something went wrong.', {
        description: 'An error occurred while deleting contact. Please try again in a moment.',

      });
    }
  };

  const handleEdit = (id) => {
    Navigate(`/contacts/edit/${id}/`)
  }

  const renderContactName = (row) => {
    const { fullName } = row;
    const { id } = row;
    return <ContactNameCell fullName={fullName} id={id} onNavigate={handleNavigateConatct} />;
  }

  const handleNavigateConatct = (id) => {
    // Navigate(`/contacts/${id}?tab=About`)
    Navigate(`/contacts/${id}`)
  }

  // Custom Render Func For Jquery Data tbale
  const renderCustomActions = (row) => {
    const { id, crm_id } = row;

    return (
      <DropdownMenu>
        {(canEditContact || canDeleteContact) && (
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="text-gray-600">
              <KeenIcon icon="dots-vertical" className="text-[18px]" />
            </Button>
          </DropdownMenuTrigger>
        )}
        <DropdownMenuContent align="end" className="w-52">
          {/* {canEditContact && crm_id === null && (
            <DropdownMenuItem onClick={() => handleSyncContactToHubspot(id, false)}>
              <KeenIcon icon="arrow-zigzag" className="mr-0.5 h-4 w-4 cursor-pointer" /> Sync to Hubspot
            </DropdownMenuItem>
          )}
          {canEditContact && crm_id !== null && (
            <DropdownMenuItem onClick={() => handleSyncContactToHubspot(id, true)}>
              <KeenIcon icon="exit-left" className="mr-0.5 h-4 w-4 cursor-pointer" /> Unsync from HubSpot
            </DropdownMenuItem>
          )} */}
          <Can I="handle" a="contact" ability={ability}>
            <DropdownMenuItem onClick={() => handleEdit(id)}>
              <KeenIcon icon="notepad-edit" className="mr-0.5 h-4 w-4 cursor-pointer" /> Edit
            </DropdownMenuItem>
          </Can>
          <Can I="delete" a="contact" ability={ability}>
            <DropdownMenuItem onClick={() => openModal(id)} className="text-red-600 cusror-pointer">
              <KeenIcon icon="trash" className="mr-0.5 h-4 w-4" /> Delete
            </DropdownMenuItem>
          </Can>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const selectAllMatching = () => {
    const table = tableRef.current?.dt?.();
    if (!table) return;
    setAllMatching({ count: table.page.info().recordsDisplay, params: table.ajax.params() });
  };

  const clearSelection = () => {
    tableRef.current?.dt?.()?.rows().deselect();
    setAllMatching(null);
  };

  const exportCount = allMatching ? allMatching.count : selectedRows.length;

  const handleExportContact = async () => {
    // Only what is selected is exported (OL-206); the server refuses an empty export too.
    const payload = exportPayload(selectedRows.map((row) => row.id), allMatching?.params);
    if (!payload) {
      toast.warning('Please select at least one contact to export.');
      return;
    }
    setExportContactLoading(true)
    try {
      const response = await dynamicExportService.dynamicExport('contact', payload);

      // Sent for approval instead of downloaded (OL-154).
      const pending = await pendingApprovalMessage(response);
      if (pending) {
        toast.info('Export sent for approval', { description: pending });
        return;
      }

      const url = window.URL.createObjectURL(response);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contacts.xlsx');
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exported Contacts.', {
        description: 'Contact Exported Successfully.',

      });
    } catch (error) {
      console.error('Error', error)
      toast.error('Failed to export Contact.', {
        description: (await exportErrorMessage(error)) || 'An error occurred while exporting contact. Please try again later.',

      });
    } finally {
      setExportContactLoading(false)
    }
  }

  useEffect(() => {
    setConfig({
      actions: (
        <>
          {canExport && (
            <button onClick={handleExportContact} disabled={exportContactLoading} className="btn btn-xs btn-sm btn-light">
              <KeenIcon icon="exit-up" />
              {exportContactLoading
                ? 'Please Wait...'
                : `Export Contacts${exportCount ? ` (${exportCount.toLocaleString()})` : ''}`}
            </button>
          )}
        </>
      ),
    });

    return () => setConfig(null);
  }, [exportContactLoading, selectedRows, allMatching, canExport]);

  return (
    <>
      <Container>
        {/* Filters */}
        <FilterContainer
          show={showFilter}
          onApply={() => refresh()}
          onReset={resetFilters}
        >
          <SelectInput
            label="Created By"
            type="users"
            value={filterData.created_by}
            onChange={(val) => handleFilterChange('created_by', val)}
            loadOptions={loadUserOptions}
            pageSize={20}
            customClass="sm:w-[32%] w-full"
          />

          <SelectInput
            isMulti={true}
            label="Assigned To"
            type="users"
            value={filterData.assigned_to}
            onChange={(val) => handleFilterChange('assigned_to', val)}
            loadOptions={loadUserOptions}
            pageSize={20}
            customClass="sm:w-[32%] w-full"
          />

          <SelectInput
            isMulti={true}
            label="Contact Type"
            type="contact_type"
            value={filterData.contact_type}
            onChange={(val) => handleFilterChange('contact_type', val)}
            loadOptions={loadContactTypeOptions}
            pageSize={20}
            customClass="sm:w-[32%] w-full"
          />

          <SelectInput
            label="Status"
            value={filterData.status}
            onChange={(val) => handleFilterChange('status', val)}
            staticOptions={STATUS_OPTIONS}
            customClass="sm:w-[32%] w-full"
          />

          <SelectInput
            label="Secure Messaging"
            value={filterData.allow_secure_messaging}
            onChange={(val) => handleFilterChange('allow_secure_messaging', val)}
            staticOptions={STATUS_NUMBER_OPTIONS}
            customClass="sm:w-[32%] w-full"
          />

          <SelectInput
            label="Tags"
            type="tags"
            isMulti={true}
            value={filterData.tags}
            onChange={(val) => handleFilterChange('tags', val)}
            loadOptions={loadTagOptions}
            pageSize={20}
            customClass="sm:w-[32%] w-full"
          />

          <DateInput
            customClass="sm:w-[32%] w-full"
            label="Created At - (From, To)"
            mode="range"
            value={filterData.date_range}
            onChange={(range) => {
              setFilterData(prev => ({
                ...prev,
                from_date: range?.from ?? null,
                to_date: range?.to ?? null,
                date_range: range,
              }));
            }}
          />
        </FilterContainer>
        <div className="min-w-full mb-4 card">
          <div className="card-header !block">
            <div className='flex flex-wrap items-center justify-between'>
              <div className='flex items-center gap-1'>
                <ColumnToggleDropdown
                  tableRef={tableRef}
                  columns={columns}
                  defaultVisibleColumns={defaultVisibleColumns}
                  storageKey={columnOrderStorageKey}
                  pinnedLastColumns={['actions']}
                />
                {/* <h3 className="card-title">Contacts</h3> */}
              </div>

              <div className='flex flex-wrap items-center gap-2'>
                <BulkDeleteActions
                  canDelete={canDeleteContact}
                  selectedCount={selectedRows.length}
                  bulkDeleteLoading={bulkDeleteLoading}
                  allSelected={allSelected}
                  onOpenConfirm={openBulkDelete}
                  onToggleSelectPage={toggleSelectPage}
                  confirmOpen={bulkDeleteOpen}
                  onCloseConfirm={closeBulkDelete}
                  onConfirmDelete={confirmBulkDelete}
                  confirmMessage={`
                    You are about to delete ${selectedRows.length} contact(s).
                    This will also delete all associated matters, invoices, and documents.
                    You can restore it from the Recycle Bin within 30 days.
                  `}
                />
                {canEditContact && selectedRows.length > 0 && (
                  <button className='text-xs btn btn-sm btn-primary btn-outline' onClick={() => setBulkEditOpen(true)}>
                    <KeenIcon icon="pencil" className="w-fit btn-icon" />
                    Edit Selected ({selectedRows.length})
                  </button>
                )}
                <BulkEditModal
                  open={bulkEditOpen}
                  title="Edit Selected Contacts"
                  selectedCount={selectedRows.length}
                  fields={bulkEditFields}
                  loading={bulkEditLoading}
                  onSubmit={handleBulkEdit}
                  onClose={() => setBulkEditOpen(false)}
                />
                <button className='text-xs btn btn-sm btn-primary btn-outline' onClick={handleToggleFilter}>
                  <KeenIcon icon="filter" className="w-fit btn-icon" />
                  {showFilter ? 'Hide Advanced Filters' : 'Advanced Filters'}
                </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="btn btn-sm btn-light btn-outline text-xs flex items-center gap-1"
                      >
                        Add Contacts
                        <ChevronDown className="size-4" />
                      </button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent className="w-38 p-1">
                      <DropdownMenuItem onClick={() => Navigate("/contacts/create")} className="text-xs cursor-pointer">
                        Create New
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => Navigate("/data-import/import", { state: { module: "contacts" } })}
                        className="text-xs cursor-pointer"
                      >
                        Import
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                {/* {canShowAddContactButton && (
                )} */}
              </div>
            </div>
          </div>

          {canExport && (allMatching || (allSelected && selectedRows.length > 0 && contactRecordsFiltered > selectedRows.length)) && (
            <div className="flex flex-wrap items-center justify-center gap-2 px-5 py-2 text-sm border-b bg-light-active">
              {allMatching ? (
                <>
                  <span>All {allMatching.count.toLocaleString()} contacts that match the current filters are selected for export.</span>
                  <button type="button" className="link" onClick={clearSelection}>Clear selection</button>
                </>
              ) : (
                <>
                  <span>All {selectedRows.length} contacts on this page are selected.</span>
                  <button type="button" className="link" onClick={selectAllMatching}>
                    Select all {contactRecordsFiltered.toLocaleString()} contacts that match the current filters
                  </button>
                </>
              )}
            </div>
          )}

          {customFieldsLoading ? (
            <div className="p-4 text-sm text-center">Loading...</div>
          ) : (
            <DataTable
              className='table-with-checkbox'
              id="contact-table"
              ajax={ajaxConfig}
              columns={columns}
              options={options}
              ref={tableRef}
            />
          )}
        </div>

        <ConfirmationModal
          show={isOpen}
          onClose={closeModal}
          onConfirm={handleDelete}
          message="Are you sure you want to delete this Contact? You can restore it from the Recycle Bin within 30 days."
          title="Are You Sure?"
        />

      </Container>
    </>
  )
}

export { Contacts }
